import { useState } from "react";
import {
    SxProps,
    Theme,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemIcon,
    Collapse,
    Divider,
} from "@mui/material";
import ArrowClosedIcon from "./icons/ArrowClosedIcon";
import ArrowOpenedIcon from "./icons/ArrowOpenedIcon";
import CircleIcon from "./icons/CircleIcon";
import { isMobile } from "react-device-detect";
import { NestedFaqTreeItem } from "../adapters/api";

export enum FaqTreeType {
    Software,
    Testing,
}

type FaqTreeListProps = {
    textFilter: string,
    tree: NestedFaqTreeItem[],
    type: FaqTreeType,
    onHoverCategory?: (images: string[] | undefined, caption: string | undefined) => void;
}

export const FaqTreeStyle: Record<string, SxProps<Theme>> = {
    container: {
        flexGrow: 1,
        textAlign: "left",
        height: "70vh",
    },
    containerMobile: {
        flexGrow: 1,
        textAlign: "left",
    },
    content: {
        padding: "40px",
        border: "1px solid white",
        borderRadius: "20px",
        height: "100%",
        overflow: "hidden",

        display: "flex",
        flexDirection: "column"
    },
    contentMobile: {
        border: "1px solid white",
        borderRadius: "20px",
        padding: "10px", 
        paddingTop: "0px"
    },
    faqIcon: {
        minWidth: 0,
        marginRight: "10px",
        justifyContent: "center",
        alignItems: "center",
        svg: {
            marginTop: "8px",
        },
        img: {
            width: "20px",
            height: "auto",
        },
    },
    faqItemTitle: {
        fontSize: "22px",
    },
    faqItemTitleMobile: {
        fontSize: "18px",
    },
    faqContent: {
        paddingLeft: "10px",
        paddingRight: "10px",
        // paddingTop: "15px"
    },
    faqContentText: {
        fontSize: "20px",
    },
    faqContentTextMobile: {
        fontSize: "17px"
    },
};

export function FaqTreeList({ textFilter, tree, type, onHoverCategory }: FaqTreeListProps) {
    const [shown, setShown] = useState<number[]>([]);

    const handleHover = (item: NestedFaqTreeItem) => {
        if (onHoverCategory != null) {
            onHoverCategory(item.images != null ? item.images : undefined, item.caption != null ? item.caption : undefined);
        }
    }

    const handleShowItem = (idx: number, item: NestedFaqTreeItem) => {
        // Apre/chiude un elemento collassabile dell'albero.
        // All'apertura, chiude gli altri elementi precedentemente aperti.
        if (shown.includes(idx)) {
            setShown([]);
        } 
        else {
            setShown([idx]);
        }
        handleHover(item);
    };

    function filterItems(itemTree: NestedFaqTreeItem[]): NestedFaqTreeItem[] {
        // Filtra gli elementi che:
        // - hanno il testo ricercato nel titolo
        // - hanno un contenuto e il contenuto include il testo ricercato
        // - hanno figli che contengono il testo ricercato
        let filteredItems = itemTree.filter(
            (item) =>
                item.title.toLowerCase().includes(textFilter.toLowerCase()) ||
                (item.content && item.content.toLowerCase().includes(textFilter.toLowerCase())) ||
                (item.children && filterItems(item.children).length > 0) ||
                (item.title != null && item.title === "Separator")
        );
        // Rimuovo i separatori dalla fine della lista
        while (
            filteredItems.length > 0 && 
            filteredItems[(filteredItems.length -1)].title === "Separator"
        ) {
            filteredItems.splice(-1, 1);
        }

        return filteredItems
    };

    return <List>
        {filterItems(tree).map((item, idx) => {
            if (item.title === "Separator") {
                return <Divider sx={{mb: 2}} key={idx}/>
                // return <hr style={{ borderTop: "1px solid grey", borderBottom: "none", marginRight: "20px", marginBottom: "25px" }} />
            }

            else if (item.title === "Title") {
                return <ListItemText
                    key={idx}
                    primary={item.content}
                    primaryTypographyProps={{
                        sx: isMobile
                            ? FaqTreeStyle.faqItemTitleMobile
                            : FaqTreeStyle.faqItemTitle,
                    }}
                />
            }

            // elementi senza content/children
            if (item.content == null && (item.children == null || item.children.length === 0)) {
                return (
                    <>
                        <ListItem disablePadding key={idx}>
                            <ListItemButton disableTouchRipple onClick={() => handleHover(item)} sx={{
                                cursor: item.images == null ? "auto" : "pointer",
                                '&:hover': {
                                backgroundColor: 'transparent', // Removes the grey overlay
                                },
                                '&.Mui-focusVisible': {
                                backgroundColor: 'transparent', // Optional: removes focus ring color
                                }
                            }}>
                                <ListItemIcon sx={FaqTreeStyle.faqIcon}>
                                    <CircleIcon
                                        color="secondary"
                                        sx={{
                                            color:
                                                type === FaqTreeType.Software
                                                    ? "software.main"
                                                    : "testing.main",
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.title}
                                    primaryTypographyProps={{
                                        sx: isMobile
                                            ? FaqTreeStyle.faqItemTitleMobile
                                            : FaqTreeStyle.faqItemTitle,
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </>
                )
            }
            
            // elementi con content/children
            return (
                <>
                    {/* Icona (freccia o pallino) e titolo */}
                    <ListItem disablePadding key={idx}>
                        <ListItemButton onClick={() => handleShowItem(idx, item)}>
                            <ListItemIcon sx={FaqTreeStyle.faqIcon}>
                                {shown.includes(idx) ? (
                                    <ArrowOpenedIcon
                                        color="secondary"
                                        sx={{
                                            color:
                                                type === FaqTreeType.Software
                                                    ? "software.main"
                                                    : "testing.main",
                                        }}
                                    />
                                ) : (
                                    <ArrowClosedIcon
                                        sx={{
                                            color:
                                                type === FaqTreeType.Software
                                                    ? "software.main"
                                                    : "testing.main",
                                        }}
                                    />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.title}
                                primaryTypographyProps={{
                                    sx: isMobile
                                        ? FaqTreeStyle.faqItemTitleMobile
                                        : FaqTreeStyle.faqItemTitle,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    <Collapse
                        sx={FaqTreeStyle.faqContent}
                        in={shown.includes(idx)}
                        key={"a"}
                    >
                        <Typography
                            sx={
                                isMobile
                                    ? FaqTreeStyle.faqContentTextMobile
                                    : FaqTreeStyle.faqContentText
                            }
                        >
                            {item.content}
                        </Typography>
                        {
                            item.children && <FaqTreeList textFilter={textFilter} tree={item.children} type={type} />
                        }
                    </Collapse>
                </>
            )
        }

        )}
    </List>
}